import { Address, Cell, OpenedContract, Sender, SenderArguments, address as toAddress } from "@ton/core";
import { TonConnectUI } from "@tonconnect/ui-react";
import { Big } from "big.js";
import { t } from "i18next";

import { ToastService } from "@/components/ToastService";
import { TOAST_ID, TON_TOKEN } from "@/constants";
import { ColoredNetworkIconMap } from "@/constants/map";
import { HapiTonAttestation } from "@/services/contracts/ton/HapiAttestation";
import { delay, formatTokenAmount, isHereWallet } from "@/utils";

import { hapiApi } from "../api/hapi";
import { publicClient } from "../contracts/ton";
import { TON_CHAIN, TON_DEFAULT_GAS, TON_MIN_COMMISSION, TON_MIN_JETTON_STORAGE } from "../contracts/ton/constants";
import { UserTonJetton } from "../contracts/ton/UserJetton";

const createTonSender = (tonClient: TonConnectUI, getMessageHash: (hash: string) => void): Sender => {
  return {
    send: async (args: SenderArguments) => {
      try {
        const result = await tonClient.sendTransaction({
          messages: [
            {
              address: args.to.toString(),
              amount: args.value.toString(),
              payload: args.body?.toBoc().toString("base64"),
            },
          ],
          validUntil: Date.now() + 5 * 60 * 1000,
        });

        const hash = Cell.fromBase64(result.boc).hash().toString("hex");
        getMessageHash(hash);
      } catch (e) {
        console.error(e);
      }
    },
  };
};

const getScoreFromJetton = async (userFriendlyAddress: string, trustScore: number) => {
  const timeInterval = 7000;
  const maxRetries = 9;
  let attempt = 0;

  let status: boolean | null = null;
  let data;
  let jettonAddress: OpenedContract<UserTonJetton> | null = null;

  while (attempt < maxRetries) {
    try {
      await delay(timeInterval);
      if (!jettonAddress) {
        const userJettonAddress = HapiTonAttestation.getStaticUserJettonAddress(userFriendlyAddress);
        jettonAddress = UserTonJetton.createFromAddress(userJettonAddress);
      }

      const localData = await jettonAddress.getAttestationData();

      if (localData.trustScore >= trustScore) {
        status = true;
        data = localData;
        break;
      } else {
        status = false;
      }
    } catch (error) {
      console.error(`Error: while get jettonAddress=${jettonAddress} or getTrustScore\n\n`, error);
    }

    attempt++;
  }

  return { status, data };
};

const mintScore = async ({
  tonConnectUI,
  address,
  signature,
  trustScore,
  expirationDate,
  manualUpdate,
  isUpdateMint = false,
}: {
  tonConnectUI: TonConnectUI;
  address: string;
  signature: string;
  trustScore: number;
  expirationDate: number;
  manualUpdate: (data: {
    commissionOwner: Address;
    trustScore: bigint;
    expirationDate: bigint;
    attestationAddress: Address;
  }) => void;
  isUpdateMint?: boolean;
}): Promise<boolean> => {
  const name = isUpdateMint ? "re-mint" : "mint";
  const icon = ColoredNetworkIconMap.TON;

  try {
    ToastService.loading({ text: t("Messages.LoadingAction", { name }), id: TOAST_ID.MINT });

    const hapiTonContract = HapiTonAttestation.createFromAddress();
    const feeAmount = isUpdateMint
      ? await hapiTonContract.getUpdateAttestationFee()
      : await hapiTonContract.getCreateAttestationFee();

    const { balance } = await publicClient.accounts.getAccount(toAddress(address));
    if (Big(balance.toString()).lte(feeAmount.toString())) {
      const formattedFee = formatTokenAmount(feeAmount.toString(), TON_TOKEN.decimals);
      ToastService.update({
        text: `Insufficient Balance - ${formattedFee} TON`,
        id: TOAST_ID.MINT,
        icon,
      });
      return false;
    }

    const signatureBuffer = Buffer.from(signature, "hex");

    let messageHash: string | null = null;

    const sender = createTonSender(tonConnectUI, (hash: string) => {
      messageHash = hash;
    });

    if (isUpdateMint) {
      await hapiTonContract.sendUpdateAttestation(sender, {
        queryId: 1,
        trustScore,
        expirationDate,
        signature: signatureBuffer,
        value: feeAmount + TON_MIN_COMMISSION + TON_MIN_JETTON_STORAGE + TON_DEFAULT_GAS,
      });
    } else {
      await hapiTonContract.sendCreateAttestation(sender, {
        queryId: 0,
        trustScore,
        expirationDate,
        signature: signatureBuffer,
        value: feeAmount + TON_MIN_COMMISSION + TON_MIN_JETTON_STORAGE + TON_DEFAULT_GAS,
      });
    }

    if (!messageHash) throw new Error("Message hash not found.");
    const chainId = isHereWallet() ? TON_CHAIN.MAINNET : -999;

    await hapiApi.logAttest({ chain: chainId, address, method: isUpdateMint ? 1 : 0 });

    const { status, data } = await getScoreFromJetton(address, trustScore);

    if (data) {
      manualUpdate(data);
    }
    if (status === null)
      throw new Error("Error decrypting transaction, may be in progress.\n Follow it on tonscan.org");

    if (status) ToastService.update({ text: `Trust score is ${name}ed`, id: TOAST_ID.MINT, icon });

    return status;
  } catch (error) {
    console.error(`Error, while ${name} trust score on TON \n\n`, error);
    if (error instanceof Error) {
      console.error({ name: error.name, message: error.message, stack: error.stack });
      ToastService.update({ text: error.message, id: TOAST_ID.MINT, icon });
    }
    return false;
  }
};

const mintNft = async () => {
  return false;
};

export default {
  mintNft,
  mintScore,
};
